import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, Auth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { isNativeApp } from '@/lib/platform';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Firestore is needed synchronously on all pages (catalog data).
// In the browser we enable offline persistence (IndexedDB) so repeat screens
// read from the local cache instead of re-hitting Firestore — fewer billed
// reads and an instant feel. On the server (Next static prerender runs in
// Node, no IndexedDB) and if persistence can't init, fall back to in-memory.
const db: Firestore = (() => {
  if (typeof window === 'undefined') return getFirestore(app);
  try {
    // Native app WebView (react-native-webview / WKWebView): IndexedDB-backed
    // persistence is unreliable there — after the first load, repeat reads hang
    // or return empty (photos/profile/my-requests vanish on navigation). So the
    // app keeps an in-memory cache + forced long-polling — rock-solid in WebView.
    if (isNativeApp()) {
      return initializeFirestore(app, {
        localCache: memoryLocalCache(),
        experimentalForceLongPolling: true,
      });
    }
    // Plain web browser: enable IndexedDB persistence so repeat screens read from
    // the local cache instead of re-hitting Firestore — faster feel AND fewer
    // billed reads (lower Firebase cost). Single-tab manager avoids multi-tab
    // issues; falls back to in-memory if persistence can't init (private mode).
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) }),
    });
  } catch {
    // Already initialized (HMR re-eval) or persistence unavailable
    // (e.g. private browsing / unsupported browser) — degrade gracefully.
    return getFirestore(app);
  }
})();

// Auth & Storage: lazy-initialize on first access to reduce initial bundle execution cost
let _auth: Auth | null = null;
let _storage: FirebaseStorage | null = null;

/**
 * Get Firebase Auth instance (lazy-initialized).
 * Persistence is set on first call.
 */
function getAuthInstance(): Auth {
  if (!_auth) {
    _auth = getAuth(app);
    // Ensure session persists across browser/app restarts (30+ days)
    if (typeof window !== 'undefined') {
      setPersistence(_auth, browserLocalPersistence).catch(console.error);
    }
  }
  return _auth;
}

/**
 * Get Firebase Storage instance (lazy-initialized).
 */
function getStorageInstance(): FirebaseStorage {
  if (!_storage) {
    _storage = getStorage(app);
  }
  return _storage;
}

// Use Proxy to maintain backward-compatible `auth` and `storage` exports
// while deferring actual initialization until first property access
const auth: Auth = new Proxy({} as Auth, {
  get(_target, prop) {
    return Reflect.get(getAuthInstance(), prop);
  },
  set(_target, prop, value) {
    return Reflect.set(getAuthInstance(), prop, value);
  },
});

const storage: FirebaseStorage = new Proxy({} as FirebaseStorage, {
  get(_target, prop) {
    return Reflect.get(getStorageInstance(), prop);
  },
  set(_target, prop, value) {
    return Reflect.set(getStorageInstance(), prop, value);
  },
});

export { app, auth, db, storage };
